import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download, FileCheck } from 'lucide-react';
import { useCockpitTitle } from '../components/EmployerCockpitLayout';
import { Button } from '../components';
import api from '../services/api';

/** Trajecten → Jaarverslagen: archief van alle ingediende jaarverslagen per professional. */
const EmployerTrajectenJaarverslagenPage: React.FC = () => {
  useCockpitTitle('Jaarverslagen', 'Hier worden de jaarverslagen van uw professionals bewaard');
  const [jaar, setJaar] = useState('');

  const { data: alle = [], isLoading } = useQuery({
    queryKey: ['employer-reports', '', ''],
    queryFn: async () => ((await api.get('/employer/reports')).data.data || []) as any[],
  });
  const jaren = (Array.from(new Set(alle.map((r) => r.jaar))) as number[]).sort((a, b) => b - a);
  const rijen = jaar ? alle.filter((r) => String(r.jaar) === jaar) : alle;

  return (
    <div className="px-4 sm:px-6 py-6 space-y-4">
      <select value={jaar} onChange={(e) => setJaar(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
        <option value="">Alle jaren</option>
        {jaren.map((y) => <option key={y} value={String(y)}>{y}</option>)}
      </select>

      {isLoading ? (
        <p className="text-gray-600 py-12 text-center">Laden…</p>
      ) : rijen.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg">
          <FileCheck className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <p className="text-gray-700 mb-1">Nog geen jaarverslagen</p>
          <p className="text-sm text-gray-500">Zodra een professional een jaarverslag indient, wordt het hier opgeslagen.</p>
        </div>
      ) : (
        <div className="overflow-x-auto bg-white rounded-lg shadow">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {['Ontvangen', 'Jaar', 'Discipline', 'Professional', 'Titel'].map((k) => (
                  <th key={k} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{k}</th>
                ))}
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Download</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {rijen.map((r) => (
                <tr key={r.id} className="hover:bg-gray-50 text-sm text-gray-900">
                  <td className="px-4 py-3 whitespace-nowrap">{new Date(r.submitted_at || r.created_at).toLocaleDateString('nl-NL')}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{r.jaar}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{r.professional_type_label || r.professional_type || '—'}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{[r.professional_first_name, r.professional_last_name].filter(Boolean).join(' ') || '—'}</td>
                  <td className="px-4 py-3 max-w-xs truncate">{r.titel || '—'}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-right">
                    <Button variant="outline" size="sm" className="flex items-center gap-1 ml-auto" onClick={() => window.open(`/api/employer/reports/${r.id}/pdf?t=${Date.now()}`, '_blank')}>
                      <Download className="w-4 h-4" />PDF
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default EmployerTrajectenJaarverslagenPage;
